import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation, Trans } from 'react-i18next'
import { adminPayments } from '../../api/adminClient'
import ConfirmModal from '../../components/admin/ConfirmModal'
import { Download } from 'lucide-react'

type PaymentItem = {
  id: string
  receipt_id: string
  amount: string
  currency: string
  status: string
  monri_transaction_id?: string
  customer_email?: string
  created_at: string
}

export default function AdminPayments() {
  const { t } = useTranslation('portal')
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState('')
  const [refundModal, setRefundModal] = useState<PaymentItem | null>(null)
  const [exporting, setExporting] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'payments', statusFilter],
    queryFn: () =>
      adminPayments
        .list({ status: statusFilter || undefined, limit: 100 })
        .then((r) => r.data),
  })

  const refund = useMutation({
    mutationFn: ({ id }: { id: string }) => adminPayments.refund(id),
    onSuccess: () => {
      setRefundModal(null)
      queryClient.invalidateQueries({ queryKey: ['admin', 'payments'] })
    },
  })

  const handleExport = async () => {
    setExporting(true)
    try {
      const { data: blob } = await adminPayments.exportCsv(statusFilter ? { status: statusFilter } : undefined)
      const url = URL.createObjectURL(blob as unknown as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'payments_export.csv'
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setExporting(false)
    }
  }

  const items = (data?.items ?? []) as PaymentItem[]
  const total = data?.total ?? 0

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('admin.payments.title')}</h1>
      <div className="flex flex-wrap gap-4 mb-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900"
        >
          <option value="">{t('admin.payments.allStatuses')}</option>
          <option value="completed">{t('admin.payments.completed')}</option>
          <option value="pending">{t('admin.payments.pending')}</option>
          <option value="failed">{t('admin.payments.failed')}</option>
          <option value="refunded">{t('admin.payments.refunded')}</option>
          <option value="cancelled">{t('admin.payments.cancelled')}</option>
        </select>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting}
          className="inline-flex items-center gap-2 rounded-lg bg-gray-100 hover:bg-gray-200 px-4 py-2 text-gray-700 text-sm disabled:opacity-50 border border-gray-300"
        >
          <Download className="w-4 h-4" /> {t('admin.payments.exportCsv')}
        </button>
      </div>
      <div className="rounded-xl bg-white border border-gray-200 overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="p-8 text-gray-500">{t('admin.common.loading')}</div>
        ) : error ? (
          <div className="p-8 text-red-600">{t('admin.payments.failedToLoad')}</div>
        ) : (
          <>
            <div className="px-4 py-2 text-gray-500 text-sm border-b border-gray-200 bg-gray-50">
              {t('admin.payments.paymentsCount', { count: total })}
            </div>
            <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500 text-sm bg-gray-50">
                  <th className="p-3">{t('admin.payments.id')}</th>
                  <th className="p-3">{t('admin.payments.amount')}</th>
                  <th className="p-3">{t('admin.common.status')}</th>
                  <th className="p-3">{t('admin.payments.customer')}</th>
                  <th className="p-3">{t('admin.common.created')}</th>
                  <th className="p-3">{t('admin.common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                    <td className="p-3 font-mono text-xs text-gray-600">{p.id.slice(0, 8)}…</td>
                    <td className="p-3 text-gray-900 font-medium">
                      {Number(p.amount).toFixed(2)} {p.currency}
                    </td>
                    <td className="p-3">
                      <span
                        className={
                          p.status === 'completed'
                            ? 'text-emerald-600'
                            : p.status === 'failed'
                              ? 'text-red-600'
                              : 'text-gray-600'
                        }
                      >
                        {p.status}
                      </span>
                    </td>
                    <td className="p-3 text-gray-600 text-sm">
                      {p.customer_email ? `${p.customer_email.slice(0, 20)}…` : '—'}
                    </td>
                    <td className="p-3 text-gray-500 text-sm">
                      {new Date(p.created_at).toLocaleString()}
                    </td>
                    <td className="p-3">
                      {p.status === 'completed' && (
                        <button
                          type="button"
                          onClick={() => setRefundModal(p)}
                          className="text-red-600 hover:underline text-sm"
                        >
                          {t('admin.payments.refund')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {items.length === 0 && (
              <div className="p-8 text-gray-500 text-center">{t('admin.payments.noPaymentsFound')}</div>
            )}
          </>
        )}
      </div>
      <ConfirmModal
        open={!!refundModal}
        title={t('admin.payments.refundTitle')}
        variant="danger"
        message={
          refundModal ? (
            <Trans
              i18nKey="portal:admin.payments.refundMessage"
              values={{ amount: Number(refundModal.amount).toFixed(2), currency: refundModal.currency }}
              components={{ strong: <strong /> }}
            />
          ) : null
        }
        confirmLabel={t('admin.payments.refund')}
        cancelLabel={t('admin.common.cancel')}
        onConfirm={() => refundModal && refund.mutate({ id: refundModal.id })}
        onCancel={() => setRefundModal(null)}
        loading={refund.isPending}
      />
    </div>
  )
}
